<template>
  <div class="period-selector">
    <div class="row align-items-center">
      <div class="col-12 col-lg-4  mb-3 mb-lg-0">
        <h5 class="mb-0"><i class="fal fa-calendar-alt"></i> Período de Análisis</h5>
      </div>

      <div class="col-12 col-lg-8">
        <div class="d-flex flex-wrap gap-2 justify-content-center justify-content-lg-end">
          <button
            v-for="period in periods"
            :key="period.value"
            type="button"
            class="btn btn-sm"
            :class="getButtonClass(period.value)"
            @click="selectPeriod(period.value)"
          >
            <i v-if="period.value === 'personalizado'" class="fal fa-calendar-plus me-1"></i>
            {{ period.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- Custom Period Selector -->
    <div v-if="showCustomSelector || selectedPeriod === 'personalizado'" class="custom-period-selector mt-3">
      <div class="row g-3">
        <div class="col-12 col-sm-6">
          <label for="startDate" class="form-label mb-2">
            <i class="fal fa-calendar-day text-warning me-2"></i> Fecha de Inicio
          </label>
          <input
            type="date"
            class="form-control"
            id="startDate"
            v-model="customStartDate"
            :max="customEndDate || maxDate"
          >
        </div>
        <div class="col-12 col-sm-6">
          <label for="endDate" class="form-label mb-2">
            <i class="fal fa-calendar-day text-warning me-2"></i> Fecha de Fin
          </label>
          <input
            type="date"
            class="form-control"
            id="endDate"
            v-model="customEndDate"
            :min="customStartDate"
            :max="maxDate"
          >
        </div>
      </div>
      <div class="row mt-4">
        <div class="col-12">
          <div class="d-flex flex-wrap gap-2 justify-content-center justify-content-sm-end">
            <button
              type="button"
              class="btn btn-success btn-sm"
              @click="applyCustomPeriod"
              :disabled="!customStartDate || !customEndDate"
            >
              <i class="fal fa-check me-2"></i> Aplicar
            </button>
            <button
              type="button"
              class="btn btn-secondary btn-sm"
              @click="cancelCustomPeriod"
            >
              <i class="fal fa-times me-2"></i> Cancelar
            </button>
          </div>
        </div>
      </div>
      <div class="row mt-2">
        <div class="col-12">
          <small class="text-muted">
            <i class="fal fa-info-circle"></i>
            <span v-if="selectedPeriod === 'personalizado' && customStartDate && customEndDate">
              Período personalizado aplicado: {{ formatDate(customStartDate) }} - {{ formatDate(customEndDate) }}
            </span>
            <span v-else>
              Selecciona un rango de fechas personalizado para analizar los datos
            </span>
          </small>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PeriodSelector',
  props: {
    period: {
      type: String,
      default: 'mes'
    }
  },
  data() {
    return {
      selectedPeriod: this.period,
      showCustomSelector: false,
      customStartDate: '',
      customEndDate: '',
      periods: [
        { value: 'semana', label: 'Semana' },
        { value: 'mes', label: 'Mes' },
        { value: 'trimestre', label: 'Trimestre' },
        { value: 'anio', label: 'Año' },
        { value: 'personalizado', label: 'Personalizado' }
      ]
    }
  },
  computed: {
    maxDate() {
      return new Date().toISOString().split('T')[0]
    }
  },
  watch: {
    period(newPeriod) {
      this.selectedPeriod = newPeriod
      if (newPeriod === 'personalizado') {
        this.showCustomSelector = true
      }
    }
  },
  methods: {
    getButtonClass(periodValue) {
      const isActive = this.selectedPeriod === periodValue
      const isCustom = periodValue === 'personalizado'

      if (isActive) {
        return isCustom ? 'btn-warning' : 'btn-primary'
      } else {
        return isCustom ? 'btn-outline-warning' : 'btn-outline-primary'
      }
    },

    selectPeriod(period) {
      this.selectedPeriod = period

      if (period === 'personalizado') {
        this.showCustomSelector = true
      } else {
        this.showCustomSelector = false
        this.$emit('period-changed', period)
      }
    },

    applyCustomPeriod() {
      if (this.customStartDate && this.customEndDate) {
        const customDates = {
          startDate: this.customStartDate,
          endDate: this.customEndDate
        }
        this.$emit('custom-period-applied', customDates)
        this.showCustomSelector = false
        // No resetear las fechas para mantener el estado
        // this.resetCustomDates()
      }
    },

    cancelCustomPeriod() {
      this.showCustomSelector = false
      this.resetCustomDates()
      // Si estaba en personalizado, volver a mes
      if (this.selectedPeriod === 'personalizado') {
        this.selectedPeriod = 'mes'
        this.$emit('period-changed', 'mes')
      }
    },

    resetCustomDates() {
      this.customStartDate = ''
      this.customEndDate = ''
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.period-selector {
  background: white;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.custom-period-selector {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e9ecef;
  border-top: 3px solid #ffc107;
}

.custom-period-selector .form-label {
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
}

.custom-period-selector .form-control {
  border-radius: 6px;
  border: 1px solid #ced4da;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.custom-period-selector .form-control:focus {
  border-color: #ffc107;
  box-shadow: 0 0 0 0.2rem rgba(255, 193, 7, 0.25);
}

.custom-period-selector .btn {
  border-radius: 6px;
  font-weight: 600;
  transition: all 0.3s ease;
  padding: 10px 20px;
  min-width: 100px;
  border: none;
}

.custom-period-selector .btn-success {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  box-shadow: 0 2px 4px rgba(40, 167, 69, 0.2);
}

.custom-period-selector .btn-success:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(40, 167, 69, 0.3);
}

.custom-period-selector .btn-secondary {
  background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
  box-shadow: 0 2px 4px rgba(108, 117, 125, 0.2);
}

.custom-period-selector .btn-secondary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(108, 117, 125, 0.3);
}

.btn-outline-primary,
.btn-outline-warning {
  border-radius: 6px;
  transition: all 0.3s ease;
  white-space: nowrap;
  font-weight: 500;
  padding: 8px 16px;
}

.btn-outline-primary:hover,
.btn-outline-warning:hover {
  transform: translateY(-1px);
}

.btn-outline-primary.active {
  background: linear-gradient(135deg, #1f4e79 0%, #2c5aa0 100%);
  border-color: #1f4e79;
  color: white;
  box-shadow: 0 2px 4px rgba(31, 78, 121, 0.3);
}

.btn-outline-warning {
  color: #222;
  border-color: #ffd000;
  background-color: transparent;
}

.btn-outline-warning:hover {
  background: linear-gradient(135deg, #ffc107 0%, #fd7e14 100%);
  border-color: #ffc107;
  color: #212529;
}

.gap-2 {
  gap: 0.5rem !important;
}
</style>
